<script setup lang="ts">
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ColorPicker from "./components/ColorPicker/index.vue";

import { ref } from "vue";
import { BorderRadius, type Radius } from "./components/BorderRadius";

import ThemeSwitch from "./components/ThemeSwitch.vue";
import Button from "@/components/ui/button/Button.vue";
import type {
    PrimaryColorValue,
    GrayColor,
} from "./components/ColorPicker/colors";
import { router } from "@inertiajs/vue3";
import { IAppearance } from "@/types/IAppearance";
import route from "ziggy-js";

const props = defineProps<{
    appearance: IAppearance;
}>();

const borderRadius = ref<Radius>(props.appearance.data.borderRadius);
const darkMode = ref<boolean>(props.appearance.data.darkMode);

const primaryColor = ref<PrimaryColorValue>(props.appearance.data.primaryColor);
const grayColor = ref<GrayColor>(props.appearance.data.grayColor);
const name = ref(props.appearance.name);

async function saveChanges() {
    try {
        await router.put(
            route("app.appearances.update", {
                appearance: props.appearance.id,
            }),
            {
                name: name.value || `${primaryColor.value}-${grayColor.value}`,
                data: {
                    primaryColor: primaryColor.value,
                    grayColor: grayColor.value,
                    borderRadius: borderRadius.value,
                    defaultSize: "mobile",
                    darkMode: darkMode.value,
                },
            },
            {
                onSuccess: () => {
                    router.visit("/appearances");
                },
            },
        );
    } catch (err) {
        console.log(err);
    }
}
</script>

<template>
    <Dialog>
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>{{ $t("appearances.edit.title") }}</DialogTitle>
                <DialogDescription>
                    <p>
                        {{ $t("appearances.edit.description") }}
                    </p>
                </DialogDescription>
            </DialogHeader>

            <div class="max-w-sm gap-1.5 grid w-full items-center">
                <Label for="name"> {{ $t("appearances.edit.name") }}</Label>
                <Input
                    id="name"
                    v-model="name"
                    type="text"
                    :placeholder="$t('appearances.edit.name_placeholder')"
                />
            </div>

            <ColorPicker
                v-model:primary="primaryColor"
                v-model:gray="grayColor"
            />

            <ThemeSwitch v-model="darkMode" />
            <BorderRadius v-model="borderRadius" />

            <DialogFooter>
                <Button @click="saveChanges">{{
                    $t("appearances.edit.edit")
                }}</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
